import { NEW_TABLE_RESET } from '../TableModel';
import type { TableConstructionProgress } from '../TableModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { NewsWidgetModel, NewsWidgetModelEventBus, NewsWidgetViewDetails } from './index';
import type { NewsSettings } from './settings';
import { Duration } from '@js-joda/core';
import { computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { hashKey } from '@tanstack/query-core';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import { RowIdField } from '@thinkalpha/table-client';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { IdeaDTO } from 'src/dtos/Idea';
import type { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import { inject, injectable } from 'src/features/ioc';
import { newsGridColumns } from 'src/features/news-list/grid/newsGridColumns';
import { createNewsList } from 'src/features/news-list/model';
import type { NewsTimeList } from 'src/features/news-list/newsAdapter';
import type { ScannerPlanDTO } from 'src/features/scanner/dtos/ScannerPlanDTO';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { ideaDTOToStage } from 'src/features/table/lib/ideaStages';
import type { TableStatus } from 'src/features/table/types';
import { TimeListColumns } from 'src/features/time-list-grid/TimeListColumns';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { enableNewsListDebugging } from 'src/lib/featureFlags/flags/enableNewsListDebugging';
import { createTable } from 'src/lib/table';
import { FilterStage, OutputStage, SourceStage, UnionStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import type { TimeListWidgetInit } from 'src/models/TimeListWidgetState';
import { TimeListWidgetState } from 'src/models/TimeListWidgetState';
import { TimeListIdeaSelection } from 'src/models/TimeListWidgetState/ideas';
import type { SlowIdeaInput } from 'src/models/TimeListWidgetState/ideas';
import { componentFailed, timeListRuntimeReducer } from 'src/models/TimeListWidgetState/runtime';
import type { TimeListRuntimeState, TimeListRuntimeAction } from 'src/models/TimeListWidgetState/runtime';
import { newsWidgetStateSchema } from 'src/models/TimeListWidgetState/schemas';
import type { NewsWidgetState } from 'src/models/TimeListWidgetState/schemas';
import { getIdeaDTOByIdQuery } from 'src/queries/ideas';
import type { ReactWidgetContainer } from 'src/ReactContainer';
import { decodeSelfOwnedWidgetState } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { FetchableTableCreationResultWithColumns } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import type { SlowIdeaSource } from 'src/types/slowIdeaSource';

// Default duration a symbol remains in the news list after being removed from the table
const DEFAULT_SYMBOL_RETENTION = Duration.ofSeconds(0);

@injectable()
export class NewsWidgetModelImpl implements NewsWidgetModel {
    static readonly stateSchema = newsWidgetStateSchema;
    static defaultState(): NewsWidgetState {
        return this.stateSchema.decode({});
    }
    static fromJSON(container: ReactWidgetContainer): NewsWidgetModelImpl {
        const { ideas, ...persisted } = decodeSelfOwnedWidgetState(this.stateSchema, container.get('GetInitialData')());
        container.bind('NewsWidgetInit').toConstantValue({
            state: { ...persisted, fastIdeasIds: ideas },
            host: {
                mode: 'workspace',
                tabId: container.get('WidgetId'),
                widgetContainer: container.get('WidgetContainerController'),
                workspaceService: container.get('WorkspaceService'),
                logger: container.get('Logger'),
            },
        });
        const model = container.get('NewsWidgetModel') as NewsWidgetModelImpl;
        void model.init().catch((error: unknown) => {
            container.get('Logger').error('Failed to initialize news widget', { error });
        });
        return model;
    }

    readonly #disposableStack = new DisposableStack();
    readonly #settings: TimeListWidgetState<NewsSettings, NewsWidgetState>;
    readonly #ideas: TimeListIdeaSelection;
    readonly #symbolFilter = computed(() => this.#settings.settings.value.symbolFilter);
    readonly #symbolRetention = computed(() => {
        const duration = this.#settings.settings.value.symbolRetention;
        return duration ? Duration.parse(duration) : DEFAULT_SYMBOL_RETENTION;
    });
    readonly #slowIdeaSourceSignal = computed(() => this.#ideas.slowSource.value);
    readonly #slowIdeaDTO = computed(() => {
        const source = this.#ideas.slowSource.value;
        return source?.type === 'idea' ? source.dto : null;
    });
    readonly #symbols = computed(() => {
        const state = this.#stateSignal.value;
        return state.type === 'ready' ? state.symbols : new Set<string>();
    });
    readonly #viewDetailsSignal = computed<NewsWidgetViewDetails>(() => {
        const state = this.#stateSignal.value;
        if (state.type === 'error') return state;
        if (state.type === 'initializing') return { type: 'loading' };
        if (state.type === 'waiting-for-symbols') return { type: 'waiting-for-symbols' };
        return {
            type: 'ready',
            instanceId: state.instanceId,
            symbols: this.#symbols,
            symbolFilter: this.#symbolFilter,
            fastIdeas: this.#ideas.fastIdeas,
        };
    });
    get settings(): ReadonlySignal<NewsSettings> {
        return this.#settings.settings;
    }
    get dirty(): ReadonlySignal<boolean> {
        return this.#settings.dirty;
    }
    toJSON(): NewsWidgetState {
        return this.#settings.toJSON();
    }
    markClean(): void {
        this.#settings.markClean();
    }
    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }

    readonly gridColumns: TimeListColumns;
    readonly newsList: NewsTimeList;
    readonly showRetirementTime: ReadonlySignal<boolean>;
    #table: ReactBindings['TableModel'];
    #slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];

    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity']);

    // New header system signals
    readonly #connectionStatus = computed(() => ({
        status: this.#table.connectionStatus.value,
        progress: this.#table.progress.value,
        failureCount: this.#table.failureCount.value,
    }));

    headerElements!: WidgetHeaderElement[];

    eventBus!: NewsWidgetModel['eventBus'];

    constructor(
        @inject('NewsWidgetInit') init: TimeListWidgetInit<NewsSettings>,
        @inject('FeatureFlagService') featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('Logger') logger: Logger,
        @inject('DefaultUniverseServiceProvider')
        defaultUniverseServiceProvider: ReactBindings['DefaultUniverseServiceProvider'],
        @inject('SlugMapServiceProvider') slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('SlugMapService') slugMapService: ReactBindings['SlugMapService'],
        @inject('TableModel') table: ReactBindings['TableModel'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('FormulaService') formulaService: ReactBindings['FormulaService'],
    ) {
        this.#disposableStack.use(table);
        this.#settings = this.#disposableStack.use(
            new TimeListWidgetState(init.host, init.state, (state) =>
                newsWidgetStateSchema.decode({ ...state, ideas: state.fastIdeasIds }),
            ),
        );

        this.#ideas = this.#disposableStack.use(
            new TimeListIdeaSelection(
                this.#settings.settings,
                (selection) => this.#settings.update(selection),
                (selection) => this.#settings.initialize(selection),
                (id) => queryClient.fetchQuery(getIdeaDTOByIdQuery(queryClient, formulaService, slugMapService, id)),
                async () => (await defaultUniverseServiceProvider()).universe.value.id,
            ),
        );
        this.gridColumns = this.#disposableStack.use(
            new TimeListColumns(newsGridColumns, {
                preferences: computed(() => this.#settings.settings.value.columns ?? []),
                update: (columns) => this.#settings.update({ columns }),
            }),
        );
        this.newsList = this.#disposableStack.use(createNewsList(queryClient));
        this.showRetirementTime = featureFlagService.getSignal(enableNewsListDebugging);
        this.#table = table;
        this.#slugMapServiceProvider = slugMapServiceProvider;

        this.#logger = logger.getSubLogger({ name: 'NewsWidgetModel' });
        if (TRACE) {
            this.#disposableStack.defer(
                effect(() => {
                    const state = this.newsList.state.value;
                    this.#logger.trace('News list scheduling state', {
                        generation: state.source.generation,
                        sourceStatus: state.source.status,
                        symbols: state.activeSymbols.length,
                        roundSymbols: state.roundSymbols.length,
                        pendingSymbols: state.pending.length,
                        viewportHeight: state.measurement.viewportHeight,
                        rows: state.rows.length,
                        fetchStatus: state.fetch.status,
                        disposed: state.disposed,
                    });
                }),
            );
        }
    }

    get fastIdeas(): ReadonlySignal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]> {
        return this.#ideas.fastIdeas;
    }

    async init() {
        const tabId = this.#settings.host.mode === 'workspace' ? this.#settings.host.tabId : undefined;
        const baseElements = tabId
            ? COMMON_HEADER_ELEMENTS
            : STANDALONE_COMMON_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
        this.headerElements = [
            ...baseElements,
            // Left side (< 50)
            { type: 'symbolPicker', position: 25, priority: 80, placeholder: 'symbol' },
            { type: 'symbolRetention', position: 28, priority: 50 },
            { type: 'helpDocument', documentId: 'news-overview', position: 30, priority: 40 },
            // Right side (> 50, spacer is at 50)
            {
                type: 'ideasMenu',
                key: 'slow',
                position: 54,
                priority: 60,

                selectType: 'single',
                ideaType: 'slow',
                ideaPlanTypes: ['universe', 'screener', 'watchlist'],
                showSelected: false,
                allowDelete: false,
            },
            {
                type: 'ideasMenu',
                key: 'fast',
                position: 56,
                priority: 60,

                selectType: 'multi',
                ideaType: 'fast',
                ideaPlanTypes: ['scanner', 'if-then'],
                showSelected: true,
                allowDelete: false,
            },
            { type: 'connectionStatus', position: 995, priority: 995 },
        ];

        this.#devtools = connectReducerDevtools({ name: `News [${tabId ?? 'standalone'}]` });
        if (this.#devtools) {
            this.#disposableStack.use(this.#devtools);
            this.#devtools.init(this.#state);
        }

        // Compose the model header with the optional workspace controls.
        this.eventBus = this.#buildEventBus();

        await this.#ideas.loadInitial();
        if (this.#disposableStack.disposed) return;
        const slugMapService = await this.#slugMapServiceProvider();
        if (this.#disposableStack.disposed) return;
        const symbolSlug = computed(() => slugMapService.slugMap.value[Slugs.symbol]);
        const changeCloseSlug = computed(() => slugMapService.slugMap.value[Slugs.symbolChangeClose]);
        this.#updateState({ type: 'INITIALIZE_SUCCESS' });

        const queryOptions: ReadonlySignal<QueryObserverOptions<FetchableTableCreationResultWithColumns>> = computed(
            () => {
                const symbolFilterValue = this.symbolFilter?.value ?? null;
                const source = this.#slowIdeaSourceSignal.value;
                const fastIdeasValue = this.#ideas.fastIdeas.value;
                return {
                    queryKey: [
                        'news-widget',
                        'table',
                        symbolFilterValue,
                        source?.type ?? null,
                        source?.id ?? null,
                        fastIdeasValue.map((idea) => idea.idea.id),
                    ],
                    queryFn: async (_context, debugIdentifier?: string) => {
                        let stage: Stage;
                        if (symbolFilterValue) {
                            stage = SourceStage.allForKey('ticker');
                        } else if (source?.type === 'idea') {
                            const allStage = SourceStage.allForKey('ticker');
                            stage = ideaDTOToStage(allStage, source.dto);
                        } else if (!source) {
                            stage = SourceStage.allForKey('ticker');
                        } else {
                            stage = SourceStage.fromTickerUniverseId(source.id);
                        }

                        if (symbolFilterValue) {
                            stage = FilterStage.fromInputStage(stage, {
                                type: 'filter',
                                formula: `symbol == "${symbolFilterValue}"`,
                                imports: [{ ...symbolSlug.value, alias: 'symbol' }],
                            });
                        } else {
                            if (fastIdeasValue.length > 1) {
                                const stages = fastIdeasValue.map((idea) => ideaDTOToStage(stage, idea));

                                if (stages.length > 0) {
                                    stage = UnionStage.from(stages);
                                }
                            } else if (fastIdeasValue.length === 1) {
                                const idea = fastIdeasValue[0];
                                stage = ideaDTOToStage(stage, idea);
                            }
                        }

                        const formula = 'changeClose > 0 ? 1 : changeClose < 0 ? -1 : 0';
                        const imports = [{ ...changeCloseSlug.value, alias: 'changeClose' }];

                        stage = OutputStage.fromInputStage(stage, {}, [
                            { id: 'symbol', type: 'indicator', indicator: symbolSlug.value },
                            { id: 'changeDirection', type: 'formula', formula, imports },
                        ]);

                        return {
                            ...(await createTable({ leafStage: stage.build() }, undefined, debugIdentifier)),
                            fetchMode: undefined,
                        };
                    },
                    enabled: true,
                    staleTime: 0,
                    gcTime: 0,
                    refetchOnWindowFocus: false,
                };
            },
        );

        // Invalidate old requests before publishing replacement table options.
        const tableOptions = signal(queryOptions.peek());
        let sourceKey: string | undefined;
        this.#disposableStack.defer(
            effect(() => {
                const options = queryOptions.value;
                const nextKey = hashKey(options.queryKey!);
                if (nextKey !== sourceKey) {
                    sourceKey = nextKey;
                    untracked(() => this.#updateState({ type: 'SOURCE_CHANGED' }));
                    this.newsList.beginSource({
                        sourceId: nextKey,
                        selectionKind: this.symbolFilter?.peek() ? 'single-symbol' : 'multi-symbol',
                        mode: 'live',
                        query: { anchor: null },
                        retention: this.symbolRetention?.peek().toMillis() ?? 0,
                    });
                }
                tableOptions.value = options;
            }),
        );
        this.#disposableStack.defer(
            effect(() => {
                this.newsList.setRetention(this.symbolRetention?.value.toMillis() ?? 0);
            }),
        );
        this.#table.init({
            initBounds: { firstRow: 0, windowSize: 100000 },
            initThrottle: 1000,
            queryOptions: tableOptions,
            devtoolsName: 'News/table',
        });

        // A complete initial selection may arrive as a snapshot or a diff with a full row-ID image.
        this.#disposableStack.use(
            this.#table.updatesWithReset$.subscribe((updates) => {
                if (!updates) return;
                if (TRACE) {
                    this.#logger.trace('News membership batch received', {
                        kind: updates === NEW_TABLE_RESET ? 'reset' : updates.type,
                        sourceStatus: this.newsList.state.peek().source.status,
                        tableKey: this.#table.key,
                    });
                }

                if (updates === NEW_TABLE_RESET) {
                    this.newsList.membershipReset();
                    this.#rowIdToSymbolMap.clear();
                    this.#unresolvedSymbolRowIds.clear();
                    this.#symbolChangeDirectionsMap.value = new Map();
                    return;
                }

                if (this.newsList.state.peek().source.status === 'awaiting-table-reset') return;
                if (updates.type === 'quote' && this.newsList.state.peek().source.status === 'awaiting-symbols') {
                    return;
                }

                if (updates.type === 'snapshot') {
                    this.#rowIdToSymbolMap.clear();
                    this.#unresolvedSymbolRowIds.clear();
                    const newSymbols = new Set<string>();
                    // Create a new Map to avoid modifying the one in the signal
                    const changeDirections = new Map<string, number>();

                    for (const update of updates) {
                        this.#rowIdToSymbolMap.set(update[RowIdField]!, update.symbol);
                        newSymbols.add(update.symbol);

                        // Get change direction directly from the changeDirection field
                        const changeDirection = Number(update['changeDirection']);
                        if (!isNaN(changeDirection)) {
                            changeDirections.set(update.symbol, changeDirection);
                        }
                    }

                    // Update the change directions signal
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                    if (this.newsList.state.peek().source.status === 'awaiting-symbols') {
                        this.newsList.activateSymbols([...newSymbols]);
                    } else this.newsList.publishSymbols([...newSymbols]);

                    this.#updateState({
                        type: 'SYMBOLS_SNAPSHOT_RECEIVED',
                        symbols: newSymbols,
                    });
                    return;
                } else if (updates.type === 'diff') {
                    const previousSymbols = new Set(this.newsList.state.peek().activeSymbols);
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.peek());
                    for (const rowId of updates.deletedRowIds) {
                        this.#rowIdToSymbolMap.delete(rowId);
                        this.#unresolvedSymbolRowIds.delete(rowId);
                    }
                    for (const rowId of updates.orderedRowIds) {
                        if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                    }
                    for (const rowId of updates.rowIdToRowIndex.keys()) {
                        if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                    }
                    for (const update of updates) {
                        const rowId = update[RowIdField]!;
                        const symbol = update.symbol;
                        if (!symbol) {
                            if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                            continue;
                        }
                        this.#rowIdToSymbolMap.set(rowId, symbol);
                        this.#unresolvedSymbolRowIds.delete(rowId);
                        const direction = Number(update.changeDirection);
                        if (!isNaN(direction)) changeDirections.set(symbol, direction);
                    }
                    // Only explicit deletions remove membership. Missing ordering entries are not removals.
                    // Keep unresolved IDs across batches until their values arrive or they are deleted.
                    const symbols = new Set(this.#rowIdToSymbolMap.values());
                    for (const symbol of changeDirections.keys()) {
                        if (!symbols.has(symbol)) changeDirections.delete(symbol);
                    }
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                    if (this.#unresolvedSymbolRowIds.size > 0) {
                        if (TRACE) {
                            this.#logger.trace('News membership awaiting complete symbol values', {
                                unresolvedRows: this.#unresolvedSymbolRowIds.size,
                                knownRows: this.#rowIdToSymbolMap.size,
                            });
                        }
                        return;
                    }
                    if (this.newsList.state.peek().source.status === 'awaiting-symbols') {
                        this.newsList.activateSymbols([...symbols]);
                        this.#updateState({ type: 'SYMBOLS_SNAPSHOT_RECEIVED', symbols });
                        return;
                    }
                    // Compare complete memberships, preserving delete/re-add and duplicate symbol rows.
                    const added = symbols.difference(previousSymbols);
                    const removed = previousSymbols.difference(symbols);
                    this.newsList.updateSymbols([...added], [...removed]);
                    this.#updateState({ type: 'SYMBOLS_UPDATE_RECEIVED', added, removed });
                } else if (updates.type === 'quote') {
                    // Create a new Map from the current change directions
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.value);

                    // Handle quote updates
                    for (const update of updates) {
                        const symbol = update.symbol;
                        if (symbol) {
                            // Get change direction directly from the changeDirection field
                            const changeDirection = Number(update['changeDirection']);
                            if (!isNaN(changeDirection)) {
                                changeDirections.set(symbol, changeDirection);
                            }
                        }
                    }

                    // Update the change directions signal
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                }
            }),
        );
    }

    #logger: Logger;

    /**
     * Builds the EventBus for the new widget header system.
     * Combines widget-specific signals/events with slice-provided ones.
     */
    #buildEventBus(): NewsWidgetModel['eventBus'] {
        let merged = MergedEventBus.from(
            EventBusBuilder.create()
                // News-specific signals
                .addSignal('widget::instrument', () =>
                    computed(() => {
                        return this.#symbolFilter.value ?? '';
                    }),
                )
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .addSignal('widget::symbolRetention', () =>
                    computed(() => {
                        return this.#symbolRetention.value;
                    }),
                )
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addSignal('widget::slowIdea', () =>
                    computed(() => {
                        return this.#slowIdeaDTO.value;
                    }),
                )
                .addSignal('widget::selectedFastIdeas', () =>
                    computed(() => {
                        return this.#ideas.fastIdeas.value.flatMap((idea) => {
                            const id = idea.idea.id;
                            return id !== undefined ? [{ type: 'idea' as const, id }] : [];
                        });
                    }),
                )
                .addSignal('widget::selectedSlowIdeas', () =>
                    computed(() => {
                        const source = this.#slowIdeaSourceSignal.value;
                        if (!source) return [];
                        return [{ type: source.type, id: source.id }];
                    }),
                )
                // News-specific events
                .addEvent('widget::instrumentChange', (payload) => {
                    this.onChangeSymbolFilter(payload.instrument);
                })
                .addEvent('widget::updateSymbolRetention', (payload) => {
                    this.onChangeSymbolRetention(Duration.parse(payload.duration));
                })
                .addEvent('widget::onIdeasSelect', (payload) => {
                    // This event is only emitted for fast ideas by the IdeasMenu component
                    // (slow ideas use widget::selectSlowIdea instead)
                    const fastIdeas = payload.ideas.filter((idea) => !('universeId' in idea)) as (
                        | IdeaDTO<ScannerPlanDTO>
                        | IdeaDTO<SearchPlanDTO>
                    )[];
                    // Always forward to handler, even empty array (to clear selection)
                    this.onSelectFastIdeas(fastIdeas);
                })
                .addEvent('widget::onIdeaDelete', () => {
                    // News widget doesn't support idea deletion
                })
                .addEvent('widget::selectSlowIdea', (payload) => {
                    // Type assertion needed because event uses generic IdeaDTO while method expects specific plan types
                    this.onSelectSlowIdeas(payload.ideas as Parameters<typeof this.onSelectSlowIdeas>[0]);
                })
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.refetch();
                })
                .build(),
        );

        const { channelSlice, widgetTabSlice } = this.#settings;
        if (channelSlice && widgetTabSlice) {
            merged = merged.merge(channelSlice.eventBus).merge(widgetTabSlice.eventBus);
        } else {
            // Standalone mode provides a name without workspace controls.
            merged = merged.merge(
                EventBusBuilder.create()
                    .addSignal('widget::name', () => signal('News'))
                    .build(),
            );
        }

        return merged as NewsWidgetModelEventBus;
    }

    #rowIdToSymbolMap = new Map<number, string>();
    #unresolvedSymbolRowIds = new Set<number>();
    #symbolChangeDirectionsMap = signal<Map<string, number>>(new Map());

    get symbolChangeDirections(): ReadonlySignal<ReadonlyMap<string, number>> {
        return this.#symbolChangeDirectionsMap;
    }

    onSelectSlowIdeas(ideas: SlowIdeaInput[]): void {
        this.#ideas.selectSlow(ideas);
    }

    onSelectFastIdeas(ideas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]): void {
        this.#ideas.selectFast(ideas);
    }

    onSelectSymbol(symbol: string) {
        if (this.#settings.host.mode === 'standalone') this.#settings.update({ symbolFilter: symbol });
        else this.#settings.onSelectSymbol(symbol);
    }

    openChartForSymbol(symbol: string) {
        this.#settings.openChartForSymbol(symbol);
    }

    openLensForSymbol(symbol: string) {
        this.#settings.openLensForSymbol(symbol);
    }

    readonly #stateSignal = signal<TimeListRuntimeState>({ type: 'initializing' });
    #devtools: ReducerDevtoolsConnection<TimeListRuntimeState, TimeListRuntimeAction> | undefined;
    get #state(): TimeListRuntimeState {
        return this.#stateSignal.value;
    }

    #updateState(action: TimeListRuntimeAction) {
        this.#stateSignal.value = timeListRuntimeReducer(this.#stateSignal.peek(), action);
        this.#devtools?.send(action, this.#stateSignal.peek());
    }

    get symbolFilter(): ReadonlySignal<string | null> {
        return this.#symbolFilter;
    }

    get symbolRetention(): ReadonlySignal<Duration> {
        return this.#symbolRetention;
    }

    // Method to change the symbol filter
    onChangeSymbolFilter(value: string) {
        // Ignore unchanged user selections, including equivalent empty values.
        const currentValue = this.#settings.peek().symbolFilter;
        if ((value === '' && currentValue === null) || value === currentValue) {
            return;
        }
        this.#settings.update({ symbolFilter: value === '' ? null : value });
        this.#settings.onSelectSymbol(value);
    }

    // Method to change the symbol retention duration
    onChangeSymbolRetention(duration: Duration) {
        this.#settings.update({ symbolRetention: duration.toString() });
    }

    get symbols(): ReadonlySignal<ReadonlySet<string>> | null {
        return this.#state.type === 'ready' ? this.#symbols : null;
    }

    get slowIdeaSource(): ReadonlySignal<SlowIdeaSource | null> {
        return this.#slowIdeaSourceSignal;
    }

    readonly runtimeError = computed(() => {
        const state = this.#stateSignal.value;
        return state.type === 'error' ? state.error : undefined;
    });

    reportComponentError(error: Error): void {
        this.#updateState(componentFailed(error));
    }

    get viewDetails(): ReadonlySignal<NewsWidgetViewDetails> {
        return this.#viewDetailsSignal;
    }

    get connectionStatus(): ReadonlySignal<TableStatus> {
        return this.#table.connectionStatus;
    }

    get progress(): ReadonlySignal<TableConstructionProgress> {
        return this.#table.progress;
    }

    refetch(): void {
        this.#table.refetch();
    }
}
